import React, { useState } from "react";
import { toast } from "sonner";
import { Search, Loader2, CalendarDays, CalendarPlus, Navigation, Pencil, XCircle } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import api, { errMsg, API } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { TIME_SLOTS } from "@/data";

const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=K%C3%B6nigstra%C3%9Fe%2036%2C%2070173%20Stuttgart";

const STATUS_STYLE = {
  pending: "bg-[rgba(234,179,8,0.15)] text-yellow-300 border-yellow-500/30",
  approved: "bg-[rgba(34,197,94,0.15)] text-green-300 border-green-500/30",
  rejected: "bg-[rgba(239,68,68,0.15)] text-red-300 border-red-500/30",
  cancelled: "bg-[rgba(239,68,68,0.12)] text-red-300/80 border-red-500/20",
  rescheduled: "bg-[rgba(59,130,246,0.15)] text-blue-300 border-blue-500/30",
};

const googleCalLink = (a) => {
  const start = new Date(`${a.date}T${a.time}:00`);
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").slice(0, 15) + "Z";
  const text = encodeURIComponent("Veloura Termin");
  const loc = encodeURIComponent("Königstraße 36, 70173 Stuttgart");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${fmt(start)}/${fmt(end)}&location=${loc}`;
};

export default function ManageAppointment() {
  const { t, lang } = useLang();
  const [ref, setRef] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [appt, setAppt] = useState(null);
  const [editing, setEditing] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");

  const lookup = async () => {
    if (!ref.trim() || !email.trim()) { toast.error(t("required_field")); return; }
    setLoading(true);
    try {
      const { data } = await api.get("/appointments/lookup", { params: { ref: ref.trim(), email: email.trim() } });
      setAppt(data);
      setNewDate(data.date);
      setNewTime(data.time);
      setEditing(false);
    } catch (e) {
      setAppt(null);
      toast.error(errMsg(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const saveChanges = async () => {
    setLoading(true);
    try {
      const { data } = await api.put(`/appointments/${appt.ref}`, { email: appt.email, date: newDate, time: newTime });
      setAppt(data);
      setEditing(false);
      toast.success(t("save_changes"));
    } catch (e) {
      toast.error(errMsg(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelAppt = async () => {
    if (!window.confirm(t("confirm_cancel"))) return;
    setLoading(true);
    try {
      await api.post(`/appointments/${appt.ref}/cancel`, { email: appt.email });
      toast.success(t("status_cancelled"));
      lookup();
    } catch (e) {
      toast.error(errMsg(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      <div className="vl-noise" />
      <Header />
      <section className="max-w-2xl mx-auto px-6 pt-32 pb-24">
        <div className="text-center mb-10">
          <h1 className="font-serif-display text-4xl font-light text-[var(--vl-ivory)]">{t("manage_title")}</h1>
          <p className="text-sm text-[var(--vl-stone)] mt-3">{t("manage_sub")}</p>
        </div>

        <div className="vl-card rounded-2xl p-6 sm:p-8 space-y-4" data-testid="lookup-form">
          <div>
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("ref_label")}</label>
            <input value={ref} onChange={(e) => setRef(e.target.value.toUpperCase())} className="vl-input rounded-lg px-4 py-3 w-full font-mono-accent" data-testid="lookup-ref-input" />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("email")}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="vl-input rounded-lg px-4 py-3 w-full" data-testid="lookup-email-input" />
          </div>
          <button onClick={lookup} disabled={loading} className="vl-btn-gold rounded-full px-7 py-3 text-sm tracking-widest uppercase font-medium flex items-center gap-2 w-full justify-center" data-testid="lookup-submit-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} {t("lookup")}
          </button>
        </div>

        {appt && (
          <div className="vl-card rounded-2xl p-6 sm:p-8 mt-6 vl-fade-up" data-testid="appointment-detail">
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono-accent text-xl text-[var(--vl-gold)] tracking-widest">{appt.ref}</span>
              <span className={`px-3 py-1 rounded-full text-xs border ${STATUS_STYLE[appt.status] || ""}`} data-testid="appt-status">
                {t(`status_${appt.status}`)}
              </span>
            </div>

            <div className="space-y-2.5">
              {[
                [t("summary_category"), t(`cat_${appt.category}`)],
                [t("summary_datetime"), `${appt.date} · ${appt.time}`],
                [t("summary_name"), appt.name],
                [t("guests"), appt.guest_count],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-[var(--vl-border)] py-2">
                  <span className="text-xs tracking-widest uppercase text-[var(--vl-stone)]">{k}</span>
                  <span className="text-sm text-[var(--vl-ivory)]">{v}</span>
                </div>
              ))}
            </div>

            {appt.admin_message && (
              <div className="mt-5 p-4 bg-[var(--vl-obsidian)] border-l-2 border-[var(--vl-gold)] rounded">
                <div className="text-[10px] tracking-widest uppercase text-[var(--vl-stone)] mb-1">{t("admin_message_label")}</div>
                <p className="text-sm text-[var(--vl-ivory)]">{appt.admin_message}</p>
              </div>
            )}

            {appt.status === "approved" && (
              <div className="flex flex-wrap gap-3 mt-6">
                <a href={`${API}/appointments/${appt.ref}/ics?email=${encodeURIComponent(appt.email)}`} className="vl-btn-gold rounded-full px-5 py-2.5 text-sm flex items-center gap-2" data-testid="download-ics-btn">
                  <CalendarPlus size={16} /> {t("add_calendar")}
                </a>
                <a href={googleCalLink(appt)} target="_blank" rel="noopener noreferrer" className="vl-btn-outline rounded-full px-5 py-2.5 text-sm flex items-center gap-2" data-testid="google-cal-btn">
                  <CalendarDays size={16} /> {t("add_google")}
                </a>
                <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="vl-btn-outline rounded-full px-5 py-2.5 text-sm flex items-center gap-2">
                  <Navigation size={16} /> {t("open_maps")}
                </a>
              </div>
            )}

            {/* modification */}
            <div className="mt-7 pt-6 border-t border-[var(--vl-border)]">
              {appt.can_modify ? (
                !editing ? (
                  <div className="flex flex-wrap gap-3">
                    <button onClick={() => setEditing(true)} className="vl-btn-outline rounded-full px-5 py-2.5 text-sm flex items-center gap-2" data-testid="edit-appt-btn">
                      <Pencil size={15} /> {t("modify_title")}
                    </button>
                    <button onClick={cancelAppt} className="rounded-full px-5 py-2.5 text-sm flex items-center gap-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 transition-colors" data-testid="cancel-appt-btn">
                      <XCircle size={15} /> {t("cancel_appt")}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="edit-form">
                    <p className="text-xs text-[var(--vl-stone)]">{t("modify_hint")}</p>
                    <input type="date" value={newDate} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} onChange={(e) => setNewDate(e.target.value)} className="vl-input rounded-lg px-4 py-3 w-full sm:w-56" data-testid="edit-date-input" />
                    <div className="flex flex-wrap gap-3">
                      {TIME_SLOTS.map((s) => (
                        <button key={s.value} onClick={() => setNewTime(s.value)} className={`px-4 py-2.5 rounded-lg border text-sm ${newTime === s.value ? "border-[var(--vl-gold)] text-[var(--vl-gold)]" : "border-[var(--vl-border)] text-[var(--vl-stone)]"}`} data-testid={`edit-time-${s.value.replace(":", "")}`}>
                          {s.value}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-3">
                      <button onClick={saveChanges} disabled={loading} className="vl-btn-gold rounded-full px-6 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="save-changes-btn">
                        {loading && <Loader2 size={15} className="animate-spin" />} {t("save_changes")}
                      </button>
                      <button onClick={() => setEditing(false)} className="vl-btn-outline rounded-full px-6 py-2.5 text-sm">{t("back")}</button>
                    </div>
                  </div>
                )
              ) : (
                appt.status !== "cancelled" && appt.status !== "rejected" && (
                  <p className="text-sm text-[var(--vl-stone)]/80">{t("cannot_modify")}</p>
                )
              )}
            </div>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
